import Card from "@material-ui/core/Card";
import Collapse from "@material-ui/core/Collapse";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { RefObject, useRef, useState } from "react";
import { useScreenSize } from "../../../../../../common/hooks/useScreenSize";
import { spaceBetween } from "../../../../../../common/util/Array/spaceBetween";
import { fetchPost } from "../../../../../../common/util/fetch";
import { PresentBox } from "../../../../../zApps/Layout/Login/MyPage/components/OpenableCards/ItemsCard/items/ItemClass/PresentBox";
import ShurikenProgress from "../../../../Animations/ShurikenProgress";
import {
    rightPanelTopBarHeight,
    rightPanelTopGap,
} from "../../../../Panel/RightPanel";
import { OtherUser, User } from "../../../../User/types";
import { usernameHeight, usernameMarginBottom } from "../../Content";
import { getMessagesPerDays } from "../../getMessagesPerDay";
import {
    MessagePanelOpenState,
    MessagesPerDays,
    UnsendMessageResponse,
} from "../../types";
import { openPresent } from "./functions";
import { MyMessage } from "./MyMessage";
import { TheirMessage } from "./TheirMessage";

export function ConversationArea({
    messages,
    user,
    targetUser,
    setMessages,
    currentlySentMessageNo,
    setCurrentlySentMessageNo,
    textareaHeight,
    showMore,
    messagesCountToShow,
    submitting,
    setSubmitting,
    panelState,
    setReplyTarget,
    inputRef,
    onlyOwnMessage,
}: {
    messages: MessagesPerDays;
    user: User;
    targetUser: OtherUser;
    setMessages: (messages: MessagesPerDays) => void;
    currentlySentMessageNo: number;
    setCurrentlySentMessageNo: (messageId: number) => void;
    textareaHeight: number;
    showMore: () => Promise<void>;
    messagesCountToShow: number;
    submitting: boolean;
    setSubmitting: (submitting: boolean) => void;
    panelState: MessagePanelOpenState;
    setReplyTarget: (text: string) => void;
    inputRef: RefObject<HTMLInputElement>;
    onlyOwnMessage: boolean;
}) {
    const { screenHeight } = useScreenSize();
    const c = useStyles({ screenHeight, textareaHeight });

    const {
        loadMoreIfNecessary,
        hasNoMessage,
        scrollableContainerRef,
        loadingMore,
    } = useEndlessScroll(messages, showMore);

    const [firstHeightTransitionFinished, setFirstHeightTransitionFinished] =
        useState(false);

    return (
        <div className={spaceBetween("relative fullWidth", c.container)}>
            <Collapse in={loadingMore} className={c.loadingMoreCollapse}>
                <ShurikenProgress
                    size={"10%"}
                    style={{ marginTop: 20, marginBottom: 100 }}
                />
            </Collapse>
            <div
                ref={scrollableContainerRef}
                className={c.scrollableContainer}
                onTransitionEnd={() => {
                    if (!firstHeightTransitionFinished) {
                        const el = scrollableContainerRef.current;
                        if (el) {
                            el.scrollTo({
                                top: el.scrollHeight,
                                behavior: "smooth",
                            });
                            setFirstHeightTransitionFinished(true);
                        }
                    }
                }}
                onScroll={loadMoreIfNecessary}
            >
                {Object.keys(messages).map((strDate, i) => {
                    const messagesPerDay = messages[strDate];
                    const allDataLoaded = messagesPerDay.some(
                        m => m.messageNo === 1
                    );
                    const isFirstDate = i === 0;
                    const filteredMessagesPerDay = onlyOwnMessage
                        ? messagesPerDay.filter(
                              m => m.fromUserId === user.userId && !m.isUnsent
                          )
                        : messagesPerDay.filter(m => !m.isUnsent);

                    if (filteredMessagesPerDay.length === 0) {
                        return null;
                    }
                    return (
                        <div key={strDate}>
                            <div className={c.dateContainer}>
                                {(!isFirstDate || allDataLoaded) && (
                                    <Card className={c.dateCard}>
                                        {strDate}
                                    </Card>
                                )}
                            </div>
                            {filteredMessagesPerDay.map(message => {
                                if (message.fromUserId === user.userId) {
                                    return (
                                        <MyMessage
                                            key={message.messageNo}
                                            me={user}
                                            theirUserId={targetUser.userId}
                                            currentlySentMessageNo={
                                                currentlySentMessageNo
                                            }
                                            message={message}
                                            unsendMessage={() => {
                                                unsendMessage(
                                                    setMessages,
                                                    messages,
                                                    strDate,
                                                    message.messageNo,
                                                    user.userId,
                                                    targetUser.userId,
                                                    messagesCountToShow
                                                );
                                            }}
                                        />
                                    );
                                }
                                return (
                                    <TheirMessage
                                        key={message.messageNo}
                                        them={targetUser}
                                        message={message}
                                        myUserId={user.userId}
                                        setMessages={setMessages}
                                        messagesCountToShow={
                                            messagesCountToShow
                                        }
                                        openPresent={async () => {
                                            if (!message.present) {
                                                return;
                                            }
                                            if (
                                                message.present ===
                                                "registered_envelope"
                                            ) {
                                                await openPresent(
                                                    targetUser.userId,
                                                    user.userId,
                                                    message.messageNo,
                                                    message.present,
                                                    message.presentQuantity,
                                                    1,
                                                    messagesCountToShow,
                                                    setMessages,
                                                    setCurrentlySentMessageNo
                                                );
                                                return;
                                            }
                                            await openPresent(
                                                targetUser.userId,
                                                user.userId,
                                                message.messageNo,
                                                message.present,
                                                new PresentBox(message.present)
                                                    .presentCoins,
                                                message.presentQuantity,
                                                messagesCountToShow,
                                                setMessages,
                                                setCurrentlySentMessageNo
                                            );
                                        }}
                                        submitting={submitting}
                                        setSubmitting={setSubmitting}
                                        targetUserIconColorNumber={
                                            panelState.targetUserIconColorNumber
                                        }
                                        setReplyTarget={setReplyTarget}
                                        inputRef={inputRef}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
                {hasNoMessage && (
                    <div className={c.noMessage}>
                        No messages yet, start the conversation!
                    </div>
                )}
            </div>
        </div>
    );
}
const useStyles = makeStyles<
    Theme,
    { screenHeight: number; textareaHeight: number }
>(theme => ({
    container: {
        margin: "0 -5px",
    },
    scrollableContainer: ({ screenHeight, textareaHeight }) => {
        const restItemsHeight =
            rightPanelTopGap +
            rightPanelTopBarHeight +
            usernameHeight +
            usernameMarginBottom;
        return {
            overflowY: "auto",
            padding: 10,
            height: screenHeight - textareaHeight - restItemsHeight,
            width: "100%",
            transition: "height 500ms",
            borderBottom: `solid 1px ${theme.palette.grey[300]}`,
        };
    },
    noMessage: {
        textAlign: "center",
        marginTop: 30,
    },
    dateContainer: { textAlign: "center", margin: "30px 0" },
    dateCard: {
        display: "inline",
        padding: "5px 30px",
        borderRadius: 20,
        backgroundColor: theme.palette.primary.light,
        color: "white",
    },
    loadingMoreCollapse: {
        position: "absolute",
        top: "0",
        display: "flex",
        alignItems: "center",
        zIndex: 1,
    },
}));

function unsendMessage(
    setMessages: (messages: MessagesPerDays) => void,
    messages: MessagesPerDays,
    strDate: string,
    messageNo: number,
    userId: number,
    targetUserId: number,
    num: number
) {
    setMessages({
        ...messages,
        [strDate]: messages[strDate].filter(m => m.messageNo !== messageNo),
    });
    fetchUnsendMessage(userId, targetUserId, messageNo, num).then(result => {
        if ("error" in result) {
            return;
        }
        setMessages(getMessagesPerDays(result.messages));
    });
}
async function fetchUnsendMessage(
    fromUserId: number,
    toUserId: number,
    messageNo: number,
    num: number
): Promise<UnsendMessageResponse> {
    return fetchPost<
        {
            fromUserId: number;
            toUserId: number;
            messageNo: number;
            num: number;
        },
        UnsendMessageResponse
    >("api/Message/UnsendMessage", { fromUserId, toUserId, messageNo, num });
}

function useEndlessScroll(
    messages: MessagesPerDays,
    showMore: () => Promise<void>
) {
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const hasNoMessage = Object.keys(messages).length === 0;
    const [loadingMore, setLoadingMore] = useState(false);

    const loadMoreIfNecessary = async () => {
        const el = scrollableContainerRef.current;
        if (!el || hasNoMessage) {
            return; // No scroll box, or no messages
        }
        const isScrollTop = el.scrollTop === 0;
        const hasScrollBar = el.scrollHeight > el.clientHeight;
        if (!isScrollTop && hasScrollBar) {
            return; // Scroll bar exists, but it is not near the top
        }
        if (Object.values(messages)[0].some(m => m.messageNo === 1)) {
            return; // All messages had already been loaded
        }

        setLoadingMore(true);
        const scrollPositionFromBottom = el.scrollHeight - el.scrollTop;
        await showMore();
        el.scrollTo({
            top: el.scrollHeight - scrollPositionFromBottom,
        });
        setLoadingMore(false);
    };

    return {
        loadMoreIfNecessary,
        hasNoMessage,
        scrollableContainerRef,
        loadingMore,
    };
}
